import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Button, Select, Spin, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import API from '@api/api';
import Utils from '@utils/utils';
import { uploadFlowImage } from '../../execution';
import MaterialLibraryModal from '../../components/MaterialLibraryModal';
import styles from './index.module.scss';

const COUNT_OPTIONS = [
    { value: 1, label: '1张' },
    { value: 2, label: '2张' },
    { value: 3, label: '3张' },
    { value: 4, label: '4张' },
];
const CLOTH_TYPE_OPTIONS = [
    { label: '上装', value: 0 },
    { label: '下装', value: 1 },
    { label: '连体装', value: 3 },
];
const MATERIAL_HD_UNIT_COST = 10;
const DEFAULT_FAST_MODE = 1;
const LINKED_IMAGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const beanIcon = require('@assets/images/arcbean3.png');

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const normalizeClothType = (value) => {
    if (value === 1 || value === '1') return 1;
    if (value === 3 || value === '3') return 3;
    return 0;
};
const normalizePersonMode = (value) => ((value === 1 || value === '1' || value === true) ? 1 : 0);

const buildInitialUploads = () => ({
    source: '',
    reference: '',
});

const UPLOAD_CARDS = [
    { key: 'source', title: '面料平铺图', placeholder: '面料平铺图' },
    { key: 'reference', title: '模特/服装图', placeholder: '模特/服装图' },
];

const MaterialHDPanel = ({
    nodeId,
    nodeData,
    onChange,
    disabled = false,
}) => {
    const fileInputRefs = useRef({});
    const dragStartedRef = useRef(false);
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const [uploads, setUploads] = useState(() => ({
        ...buildInitialUploads(),
        source: panelState.uploads?.source || inputParams.srcImage || '',
        reference: panelState.uploads?.reference || inputParams.refImage || inputParams.commodityImage || '',
    }));
    const [clothType, setClothType] = useState(
        normalizeClothType(panelState.clothType ?? inputParams.clothType ?? 0),
    );
    const [mode, setMode] = useState(
        normalizePersonMode(panelState.mode ?? inputParams.mode ?? 0),
    );
    const [generateCount, setGenerateCount] = useState(panelState.num ?? inputParams.num ?? 4);
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [materialTargetSlot, setMaterialTargetSlot] = useState('reference');
    const [draggingSlotKey, setDraggingSlotKey] = useState('');
    const [dragOverSlotKey, setDragOverSlotKey] = useState('');
    const [uploadingSlotKey, setUploadingSlotKey] = useState('');
    const lastSubmittedRef = useRef('');
    const skipNextSubmitRef = useRef(false);
    const lastExternalSyncSignatureRef = useRef('');
    const lastCheckedReferenceRef = useRef('');
    const linkedSlotSources = useMemo(() => (
        nodeData?.panelState?.linkedSlotSources
        && typeof nodeData.panelState.linkedSlotSources === 'object'
        && !Array.isArray(nodeData.panelState.linkedSlotSources)
            ? nodeData.panelState.linkedSlotSources
            : {}
    ), [nodeData?.panelState?.linkedSlotSources]);
    const costBean = useMemo(() => generateCount * MATERIAL_HD_UNIT_COST, [generateCount]);

    useEffect(() => {
        const nextUploads = {
            ...buildInitialUploads(),
            source: panelState.uploads?.source || inputParams.srcImage || '',
            reference: panelState.uploads?.reference || inputParams.refImage || inputParams.commodityImage || '',
        };
        const nextClothType = normalizeClothType(panelState.clothType ?? inputParams.clothType ?? 0);
        const nextMode = normalizePersonMode(panelState.mode ?? inputParams.mode ?? 0);
        const nextGenerateCount = panelState.num ?? inputParams.num ?? 4;
        const externalSyncSignature = JSON.stringify({
            uploads: nextUploads,
            clothType: nextClothType,
            mode: nextMode,
            generateCount: nextGenerateCount,
        });

        if (lastExternalSyncSignatureRef.current === externalSyncSignature) {
            return;
        }
        lastExternalSyncSignatureRef.current = externalSyncSignature;

        const shouldSyncFromNode = (
            !isSameValue(uploads, nextUploads)
            || clothType !== nextClothType
            || mode !== nextMode
            || generateCount !== nextGenerateCount
        );

        if (shouldSyncFromNode) {
            skipNextSubmitRef.current = true;
        }

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setClothType((prev) => (prev === nextClothType ? prev : nextClothType));
        setMode((prev) => (prev === nextMode ? prev : nextMode));
        setGenerateCount((prev) => (prev === nextGenerateCount ? prev : nextGenerateCount));
    }, [
        clothType,
        generateCount,
        inputParams.clothType,
        inputParams.commodityImage,
        inputParams.mode,
        inputParams.num,
        inputParams.refImage,
        inputParams.srcImage,
        mode,
        panelState.num,
        panelState.clothType,
        panelState.mode,
        panelState.uploads?.reference,
        panelState.uploads?.source,
        uploads,
    ]);

    useEffect(() => {
        const referenceImageUrl = uploads.reference || linkedSlotSources?.reference?.url || '';
        const isPlaceholder = !referenceImageUrl
            || referenceImageUrl === FLOW_LINK_PLACEHOLDER_ICON
            || referenceImageUrl === LINKED_IMAGE_ICON;

        if (isPlaceholder) {
            lastCheckedReferenceRef.current = '';
            return;
        }

        if (lastCheckedReferenceRef.current === referenceImageUrl) {
            return;
        }

        lastCheckedReferenceRef.current = referenceImageUrl;
        let cancelled = false;

        const syncReferenceMeta = async () => {
            try {
                const personCheckRes = await API.personCheck({
                    imageUrl: referenceImageUrl,
                });
                if (cancelled || personCheckRes?.code !== 0) return;

                const isPerson = !!personCheckRes?.data;
                setMode(isPerson ? 1 : 0);

                if (isPerson) return;

                const clothTypeRes = await API.getClothType({
                    imageUrl: referenceImageUrl,
                });
                if (cancelled || clothTypeRes?.code !== 0) return;

                setClothType(normalizeClothType(clothTypeRes?.data));
            } catch (error) {
                if (!cancelled) {
                    console.error(error);
                }
            }
        };

        syncReferenceMeta();

        return () => {
            cancelled = true;
        };
    }, [linkedSlotSources?.reference?.url, uploads.reference]);

    useEffect(() => {
        const hiddenSlotKeys = ['match'];
        const shouldReset = hiddenSlotKeys.some((slotKey) => (
            !!panelState?.uploads?.[slotKey] || !!linkedSlotSources?.[slotKey]?.edgeId
        ));

        if (!shouldReset) return;

        hiddenSlotKeys.forEach((slotKey) => {
            const linkedState = linkedSlotSources?.[slotKey];
            if (linkedState?.edgeId) {
                nodeData?.onRemoveLinkedInput?.(nodeId, slotKey, linkedState);
            }
        });
    }, [linkedSlotSources, nodeData, nodeId, panelState?.uploads]);

    const handleUploadChange = useCallback((slotKey, nextValue) => {
        setUploads((prev) => ({
            ...prev,
            [slotKey]: nextValue,
        }));
    }, []);

    const handleUploadedValue = useCallback((slotKey, nextValue) => {
        const linkedState = linkedSlotSources?.[slotKey];
        if (linkedState?.edgeId) {
            skipNextSubmitRef.current = true;
            nodeData?.onReplaceLinkedInput?.(nodeId, slotKey, linkedState, nextValue);
        }
        handleUploadChange(slotKey, nextValue);
    }, [handleUploadChange, linkedSlotSources, nodeData, nodeId]);

    const isUploadSlotOccupied = useCallback((slotKey) => (
        !!uploads?.[slotKey]
        || !!linkedSlotSources?.[slotKey]?.edgeId
    ), [linkedSlotSources, uploads]);

    const handleUploadDragStart = useCallback((slotKey, event) => {
        if (disabled || !isUploadSlotOccupied(slotKey)) return;
        dragStartedRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        setDraggingSlotKey(slotKey);
        setDragOverSlotKey('');
    }, [disabled, isUploadSlotOccupied]);

    const handleUploadDragEnd = useCallback(() => {
        window.setTimeout(() => {
            dragStartedRef.current = false;
        }, 0);
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, []);

    const handleUploadDragOver = useCallback((slotKey, event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (hasFile) {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = 'copy';
            setDragOverSlotKey(slotKey);
            return;
        }

        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setDragOverSlotKey(slotKey);
    }, [draggingSlotKey, isUploadSlotOccupied]);

    const handleUploadDragLeave = useCallback((slotKey) => {
        setDragOverSlotKey((prev) => (prev === slotKey ? '' : prev));
    }, []);

    const handleUploadDrop = useCallback(async (slotKey, event) => {
        if (uploadingSlotKey) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find((file) => file?.type?.startsWith('image/'));

        if (imageFile) {
            event.preventDefault();
            event.stopPropagation();
            flushSync(() => setUploadingSlotKey(slotKey));
            try {
                const fileUrl = await uploadFlowImage(imageFile);
                if (fileUrl) {
                    handleUploadedValue(slotKey, fileUrl);
                }
            } catch (error) {
                message.error('上传失败，请稍后重试');
            } finally {
                setUploadingSlotKey('');
                setDragOverSlotKey('');
            }
            return;
        }

        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        nodeData?.onSwapInputSlots?.(nodeId, draggingSlotKey, slotKey);
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, [draggingSlotKey, handleUploadedValue, isUploadSlotOccupied, nodeData, nodeId, uploadingSlotKey]);

    useEffect(() => {
        if (draggingSlotKey && !isUploadSlotOccupied(draggingSlotKey)) {
            setDraggingSlotKey('');
            setDragOverSlotKey('');
        }
    }, [draggingSlotKey, isUploadSlotOccupied]);

    const handleOpenLocalPicker = useCallback((slotKey, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (disabled || uploadingSlotKey) return;

        const inputElement = fileInputRefs.current?.[slotKey];
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [disabled, uploadingSlotKey]);

    const handleFileChange = useCallback(async (slotKey, event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file || disabled || uploadingSlotKey) return;

        flushSync(() => setUploadingSlotKey(slotKey));
        try {
            const fileUrl = await uploadFlowImage(file);
            if (fileUrl) {
                handleUploadedValue(slotKey, fileUrl);
            }
        } catch (error) {
            message.error('上传失败，请稍后重试');
        } finally {
            setUploadingSlotKey('');
        }
    }, [disabled, handleUploadedValue, uploadingSlotKey]);

    const handleOpenMaterialLibrary = useCallback((slotKey, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (disabled) return;
        setMaterialTargetSlot(slotKey);
        setMaterialModalOpen(true);
    }, [disabled]);

    const handleSelectMaterial = useCallback((imageUrlOrUrls) => {
        const nextImageUrls = Array.isArray(imageUrlOrUrls) ? imageUrlOrUrls.filter(Boolean) : [imageUrlOrUrls].filter(Boolean);
        if (nextImageUrls.length === 0) return;
        handleUploadedValue(materialTargetSlot, nextImageUrls[0]);
    }, [handleUploadedValue, materialTargetSlot]);

    const handleRemoveUpload = useCallback((slotKey, event) => {
        event.preventDefault();
        event.stopPropagation();

        const linkedState = linkedSlotSources?.[slotKey];
        if (linkedState?.edgeId) {
            nodeData?.onRemoveLinkedInput?.(nodeId, slotKey, linkedState);
            skipNextSubmitRef.current = true;
        }

        handleUploadChange(slotKey, '');
    }, [handleUploadChange, linkedSlotSources, nodeData, nodeId]);

    const handleGenerate = useCallback(() => {
        if (!uploads.source) {
            message.warning('请先上传面料平铺图');
            return;
        }
        if (!uploads.reference) {
            message.warning('请先上传模特/服装图');
            return;
        }

        nodeData?.onExecute?.(nodeId, { count: generateCount });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [generateCount, nodeData, nodeId, uploads.reference, uploads.source]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (nodeData?.functionKey !== 'materialHD') return;
        if (skipNextSubmitRef.current) {
            skipNextSubmitRef.current = false;
            return;
        }

        const currentLinkedSlotSources = (
            nodeData?.panelState?.linkedSlotSources
            && typeof nodeData.panelState.linkedSlotSources === 'object'
            && !Array.isArray(nodeData.panelState.linkedSlotSources)
        )
            ? nodeData.panelState.linkedSlotSources
            : {};
        const nextLinkedSlotSources = Object.fromEntries(
            Object.entries(currentLinkedSlotSources).filter(([slotKey, linkedState]) => (
                ['source', 'reference'].includes(slotKey)
                && (
                    (!!linkedState?.edgeId && uploads?.[slotKey] !== '')
                    || (uploads?.[slotKey] && uploads[slotKey] === (linkedState?.url || ''))
                )
            )),
        );

        const nextPatch = {
            inputParams: {
                srcImage: uploads.source || '',
                refImage: uploads.reference || '',
                mode,
                clothType,
                fastMode: DEFAULT_FAST_MODE,
                num: generateCount,
            },
            panelState: {
                uploads,
                linkedSlotSources: nextLinkedSlotSources,
                mode,
                clothType,
                extraSwitchValues: { fastMode: DEFAULT_FAST_MODE },
                num: generateCount,
            },
        };
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [
        clothType,
        generateCount,
        mode,
        nodeData?.inputParams,
        nodeData?.panelState,
        nodeId,
        onChange,
        uploads,
    ]);

    const canGenerate = !disabled
        && String(nodeData?.latestExecution?.status || nodeData?.executionStatus || '').toLowerCase() !== 'processing'
        && (!!uploads.reference || !!linkedSlotSources?.reference?.edgeId)
        && (!!uploads.source || !!linkedSlotSources?.source?.edgeId);
    const isGenerating = String(
        nodeData?.latestExecution?.status
        || nodeData?.executionStatus
        || 'idle',
    ).toLowerCase() === 'processing';

    return (
        <>
            <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>
                <div className={styles.uploadRow}>
                    {UPLOAD_CARDS.map((card) => {
                        const linkedState = linkedSlotSources?.[card.key];
                        const previewValue = uploads[card.key] || linkedState?.url || '';
                        const isLinkedInput = !!linkedState?.edgeId;
                        const isLinkedPlaceholder = previewValue === FLOW_LINK_PLACEHOLDER_ICON;
                        const hasResolvedLinkedPreview = isLinkedInput && !!previewValue && !isLinkedPlaceholder;
                        const previewUrl = (
                            previewValue
                            && previewValue !== FLOW_LINK_PLACEHOLDER_ICON
                            && previewValue !== LINKED_IMAGE_ICON
                        ) ? Utils.getImgThumbnail(previewValue) : '';

                        return (
                            <div key={card.key} className={styles.cardColumn}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={[
                                        styles.uploadCard,
                                        isUploadSlotOccupied(card.key) ? styles.uploadCardDraggable : '',
                                        draggingSlotKey === card.key ? styles.uploadCardDragging : '',
                                        dragOverSlotKey === card.key ? styles.uploadCardDragOver : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={(event) => {
                                        if (dragStartedRef.current) {
                                            dragStartedRef.current = false;
                                            event.preventDefault();
                                            return;
                                        }
                                        handleOpenLocalPicker(card.key, event);
                                    }}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    draggable={!disabled && isUploadSlotOccupied(card.key)}
                                    onDragStart={(event) => handleUploadDragStart(card.key, event)}
                                    onDragEnd={handleUploadDragEnd}
                                    onDragOver={(event) => handleUploadDragOver(card.key, event)}
                                    onDragLeave={() => handleUploadDragLeave(card.key)}
                                    onDrop={(event) => handleUploadDrop(card.key, event)}
                                >
                                    {previewUrl ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedPlaceholder ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={FLOW_LINK_PLACEHOLDER_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : hasResolvedLinkedPreview ? (
                                        <>
                                            <img src={previewUrl} alt={card.title} className={styles.preview} draggable={false} />
                                            <div className={styles.previewTitle}>{card.placeholder}</div>
                                        </>
                                    ) : isLinkedInput ? (
                                        <div className={styles.linkedPlaceholder}>
                                            <img
                                                src={LINKED_IMAGE_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {linkedState?.imageSequenceLabel || ''}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <PlusOutlined className={styles.plusIcon} />
                                            <span className={styles.placeholderText}>{card.placeholder}</span>
                                        </div>
                                    )}
                                    {previewValue ? (
                                        <div className={styles.filledCardActions}>
                                            <button
                                                type="button"
                                                className={`${styles.removeButton} ${(isLinkedPlaceholder || isLinkedInput) ? styles.removeButtonPlaceholder : ''}`}
                                                onClick={(event) => handleRemoveUpload(card.key, event)}
                                                aria-label={`删除${card.title}`}
                                            >
                                                <DeleteOutlined className={styles.removeIcon} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.sourceCardActions}>
                                            <Button
                                                size="small"
                                                className={styles.sourceActionButton}
                                                onClick={(event) => handleOpenLocalPicker(card.key, event)}
                                            >
                                                本地上传
                                            </Button>
                                            {card.key !== 'source' ? (
                                                <Button
                                                    size="small"
                                                    className={styles.sourceActionButton}
                                                    onClick={(event) => handleOpenMaterialLibrary(card.key, event)}
                                                >
                                                    素材库选择
                                                </Button>
                                            ) : null}
                                        </div>
                                    )}
                                    {uploadingSlotKey === card.key ? (
                                        <div className={styles.uploadLoading} aria-label={`${card.title}上传中`}>
                                            <Spin size="small" />
                                        </div>
                                    ) : null}
                                </div>
                                <input
                                    ref={(node) => {
                                        fileInputRefs.current[card.key] = node;
                                    }}
                                    type="file"
                                    accept="image/*"
                                    className={styles.hiddenInput}
                                    onChange={(event) => handleFileChange(card.key, event)}
                                />
                            </div>
                        );
                    })}
                </div>

                <div className={styles.selectRow}>
                    <span className={styles.selectLabel}>需换面料区域</span>
                    <Select
                        value={clothType}
                        onChange={setClothType}
                        options={CLOTH_TYPE_OPTIONS}
                        className={styles.areaSelect}
                        popupMatchSelectWidth
                        getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                    />
                </div>

                <div className={styles.footer}>
                    <Select
                        value={generateCount}
                        onChange={setGenerateCount}
                        options={COUNT_OPTIONS}
                        popupMatchSelectWidth={false}
                        className={styles.countSelect}
                        variant="borderless"
                        getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                    />
                    <Button
                        type="primary"
                        className={styles.generateButton}
                        onClick={canGenerate ? handleGenerate : undefined}
                        disabled={!canGenerate}
                    >
                        <span className={styles.generateButtonContent}>
                            <span>{isGenerating ? '生成中' : '生成'}</span>
                            {!isGenerating ? <img src={beanIcon} alt="" className={styles.beanIcon} draggable={false} /> : null}
                            {!isGenerating ? <span>{costBean}</span> : null}
                        </span>
                    </Button>
                </div>
            </div>
            <MaterialLibraryModal
                open={materialModalOpen}
                onClose={() => setMaterialModalOpen(false)}
                onSelect={handleSelectMaterial}
                multiple
                maxSelectCount={1}
                showApplyAction={false}
            />
        </>
    );
};

export default MaterialHDPanel;
